import { motion, AnimatePresence } from "framer-motion";
import { Search, Zap, Archive, Calendar, ChevronDown, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getTypeMeta, formatArchiveDate, ARCHIVE_THRESHOLD } from "./streamModel";
import { useArchive } from "./useStreamFeed";

const ArchivedItem = ({ item, idx }) => {
  const meta = getTypeMeta(item.type || item.priority, "viewed");
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card-light rounded-xl border border-white/10 p-3 opacity-60" data-testid={`archived-item-${idx}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium uppercase tracking-wider text-ism-teal/70 flex items-center gap-1.5"><meta.Icon className="h-3 w-3" />{meta.label}</span>
        <span className="text-xs text-gray-600">{new Date(item.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
      </div>
      <p className="text-sm text-gray-400 leading-relaxed">{item.description || item.text}</p>
    </motion.div>
  );
};

const DateGroup = ({ group, expanded, onToggle }) => (
  <div className="border border-gray-800 rounded-lg overflow-hidden">
    <button onClick={() => onToggle(group.date)} className="w-full flex items-center justify-between p-3 bg-gray-800/30 hover:bg-gray-800/50 transition-colors" data-testid={`archive-date-${group.date}`}>
      <div className="flex items-center gap-2">
        <Calendar className="h-4 w-4 text-gray-500" />
        <span className="text-sm font-medium text-gray-300">{formatArchiveDate(group.date)}</span>
        <span className="text-xs text-gray-500">({group.items.length} items)</span>
      </div>
      {expanded ? <ChevronDown className="h-4 w-4 text-gray-500" /> : <ChevronRight className="h-4 w-4 text-gray-500" />}
    </button>
    <AnimatePresence>
      {expanded && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="p-3 space-y-2 bg-gray-900/20">
          {group.items.map((item, idx) => <ArchivedItem key={item.activityId || idx} item={item} idx={idx} />)}
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export const ArchiveView = ({ userId, active, search, setSearch }) => {
  const archive = useArchive(userId, active, search);
  return (
    <div className="flex-1 flex flex-col relative z-10">
      <div className="mb-4 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
        <Input type="text" placeholder="Search archived items..." value={search} onChange={(e) => setSearch(e.target.value)}
          className="pl-10 bg-gray-800/50 border-gray-700 text-gray-300 placeholder:text-gray-500" data-testid="archive-search" />
      </div>
      <ScrollArea className="flex-1 pr-4">
        {archive.loading ? (
          <div className="flex items-center justify-center py-12">
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}><Zap className="h-6 w-6 text-gray-500" /></motion.div>
          </div>
        ) : archive.groups.length > 0 ? (
          <div className="space-y-4">
            {archive.groups.map((group) => <DateGroup key={group.date} group={group} expanded={archive.expandedDates[group.date]} onToggle={archive.toggleDate} />)}
          </div>
        ) : (
          <div className="text-center py-12">
            <Archive className="h-12 w-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">{search ? "No archived items match your search" : "No archived items yet"}</p>
            <p className="text-gray-600 text-sm mt-2">Items are automatically archived after {ARCHIVE_THRESHOLD} are reviewed</p>
          </div>
        )}
      </ScrollArea>
    </div>
  );
};
